/**
 * Platform actions invoked in ContextKit via the `invoke-platform-action` IPC message.
 * Keep in sync with `ContextKit/Sources/observer/lib/PlatformActions/`.
 */

import { z } from 'zod';

export enum PlatformActionName {
  CreateReminder = 'create-reminder',
  FetchReminders = 'fetch-reminders',
  FindReminder = 'find-reminder',
  UpdateReminder = 'update-reminder',
  CompleteReminder = 'complete-reminder',
  DeleteReminder = 'delete-reminder',
  FetchContacts = 'fetch-contacts',
  FindContact = 'find-contact',
  CreateContact = 'create-contact',
  UpdateContact = 'update-contact',
  DeleteContact = 'delete-contact',
  ListDesktopWindows = 'list-desktop-windows',
  ReadDesktopWindow = 'read-desktop-window',
  CaptureDesktopWindow = 'capture-desktop-window',
}

export const CreateReminderActionPayloadSchema = z.object({
  title: z.string().min(1),
  dueDate: z.string().datetime().optional(),
  notes: z.string().optional(),
});
export type CreateReminderActionPayload = z.infer<
  typeof CreateReminderActionPayloadSchema
>;

export const FetchRemindersActionPayloadSchema = z.object({
  list: z.string().optional(),
  includeCompleted: z.boolean(),
});
export type FetchRemindersActionPayload = z.infer<
  typeof FetchRemindersActionPayloadSchema
>;

export const FindReminderActionPayloadSchema = z
  .object({
    reminderId: z.string().optional(),
    title: z.string().optional(),
  })
  .refine((value) => value.reminderId || value.title, {
    message: 'Either reminderId or title must be provided',
  });
export type FindReminderActionPayload = z.infer<
  typeof FindReminderActionPayloadSchema
>;

export const UpdateReminderActionPayloadSchema = z.object({
  reminderId: z.string().min(1),
  title: z.string().optional(),
  notes: z.string().optional(),
  dueDate: z.string().datetime().optional(),
});
export type UpdateReminderActionPayload = z.infer<
  typeof UpdateReminderActionPayloadSchema
>;

export const CompleteReminderActionPayloadSchema = z.object({
  reminderId: z.string().min(1),
});
export type CompleteReminderActionPayload = z.infer<
  typeof CompleteReminderActionPayloadSchema
>;

export const DeleteReminderActionPayloadSchema = z.object({
  reminderId: z.string().min(1),
});
export type DeleteReminderActionPayload = z.infer<
  typeof DeleteReminderActionPayloadSchema
>;

export const PlatformActionInvocationPayloadSchema = z.object({
  action: z.nativeEnum(PlatformActionName),
  /** JSON-encoded action arguments*/
  arguments: z.string(),
});

export const CreateReminderActionResultSchema = z.object({
  reminderId: z.string(),
});
export type CreateReminderActionResult = z.infer<
  typeof CreateReminderActionResultSchema
>;

export const ReminderItemSchema = z.object({
  reminderId: z.string(),
  title: z.string(),
  notes: z.string().nullable(),
  dueDateISO: z.string().nullable(),
  isCompleted: z.boolean(),
  list: z.string().nullable(),
});

export const FetchRemindersActionResultSchema = z.object({
  reminders: z.array(ReminderItemSchema),
});
export type FetchRemindersActionResult = z.infer<
  typeof FetchRemindersActionResultSchema
>;

export const FindReminderActionResultSchema = z.object({
  reminders: z.array(ReminderItemSchema),
});
export type FindReminderActionResult = z.infer<
  typeof FindReminderActionResultSchema
>;

export const UpdateReminderActionResultSchema = z.object({
  reminderId: z.string(),
});
export type UpdateReminderActionResult = z.infer<
  typeof UpdateReminderActionResultSchema
>;

export const CompleteReminderActionResultSchema = z.object({
  reminderId: z.string(),
});
export type CompleteReminderActionResult = z.infer<
  typeof CompleteReminderActionResultSchema
>;

export const DeleteReminderActionResultSchema = z.object({
  reminderId: z.string(),
});
export type DeleteReminderActionResult = z.infer<
  typeof DeleteReminderActionResultSchema
>;

export const ContactLabeledValueSchema = z.object({
  /** "home", "work", "mobile", or a custom label */
  label: z.string().nullish(),
  value: z.string().min(1),
});

export const ContactPostalAddressSchema = z.object({
  label: z.string().nullish(),
  street: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  postalCode: z.string().nullish(),
  country: z.string().nullish(),
});

export const ContactItemSchema = z.object({
  contactId: z.string(),
  displayName: z.string(),
  givenName: z.string().nullish(),
  middleName: z.string().nullish(),
  familyName: z.string().nullish(),
  nickname: z.string().nullish(),
  organizationName: z.string().nullish(),
  departmentName: z.string().nullish(),
  jobTitle: z.string().nullish(),
  /** "YYYY-MM-DD", or "MM-DD" for year-less birthdays */
  birthday: z.string().nullish(),
  phoneNumbers: z.array(ContactLabeledValueSchema),
  emailAddresses: z.array(ContactLabeledValueSchema),
  postalAddresses: z.array(ContactPostalAddressSchema),
  urlAddresses: z.array(ContactLabeledValueSchema),
});

export const FetchContactsActionPayloadSchema = z.object({
  /** Native side defaults to 200 when omitted. */
  limit: z.number().int().positive().optional(),
});
export type FetchContactsActionPayload = z.infer<
  typeof FetchContactsActionPayloadSchema
>;

export const FetchContactsActionResultSchema = z.object({
  contacts: z.array(ContactItemSchema),
  /** Total contacts in the store, so truncation by `limit` is visible. */
  totalCount: z.number().int(),
});
export type FetchContactsActionResult = z.infer<
  typeof FetchContactsActionResultSchema
>;

export const FindContactActionPayloadSchema = z
  .object({
    contactId: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
    phoneNumber: z.string().min(1).optional(),
    email: z.string().min(1).optional(),
  })
  .refine(
    (value) =>
      [value.contactId, value.name, value.phoneNumber, value.email].filter(
        Boolean
      ).length === 1,
    {
      message: 'Provide exactly one of contactId, name, phoneNumber, or email',
    }
  );
export type FindContactActionPayload = z.infer<
  typeof FindContactActionPayloadSchema
>;

export const FindContactActionResultSchema = z.object({
  contacts: z.array(ContactItemSchema),
});
export type FindContactActionResult = z.infer<
  typeof FindContactActionResultSchema
>;

export const CreateContactActionPayloadSchema = z
  .object({
    givenName: z.string().optional(),
    familyName: z.string().optional(),
    organizationName: z.string().optional(),
    phoneNumbers: z.array(ContactLabeledValueSchema).optional(),
    emailAddresses: z.array(ContactLabeledValueSchema).optional(),
  })
  .refine(
    (value) => value.givenName || value.familyName || value.organizationName,
    {
      message:
        'At least one of givenName, familyName, or organizationName is required',
    }
  );
export type CreateContactActionPayload = z.infer<
  typeof CreateContactActionPayloadSchema
>;

export const CreateContactActionResultSchema = z.object({
  contactId: z.string(),
});
export type CreateContactActionResult = z.infer<
  typeof CreateContactActionResultSchema
>;

/**
 * Omitted fields are left untouched. A present multi-value array replaces the
 * whole list (empty array clears); a present scalar replaces the value
 * (empty string clears).
 */
export const UpdateContactActionPayloadSchema = z.object({
  contactId: z.string().min(1),
  givenName: z.string().optional(),
  familyName: z.string().optional(),
  organizationName: z.string().optional(),
  phoneNumbers: z.array(ContactLabeledValueSchema).optional(),
  emailAddresses: z.array(ContactLabeledValueSchema).optional(),
});
export type UpdateContactActionPayload = z.infer<
  typeof UpdateContactActionPayloadSchema
>;

export const UpdateContactActionResultSchema = z.object({
  /** The contact re-fetched after the save — actual post-write state. */
  contact: ContactItemSchema,
});
export type UpdateContactActionResult = z.infer<
  typeof UpdateContactActionResultSchema
>;

export const DeleteContactActionPayloadSchema = z.object({
  contactId: z.string().min(1),
});
export type DeleteContactActionPayload = z.infer<
  typeof DeleteContactActionPayloadSchema
>;

export const DeleteContactActionResultSchema = z.object({
  contactId: z.string(),
});
export type DeleteContactActionResult = z.infer<
  typeof DeleteContactActionResultSchema
>;
